import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type { ProjectReport as ProjectReportType } from "@/services/chat-service";
import type { ShortlistData, ShortlistReadiness } from "@/services/project-report-service";
import { getProjectReport, getProjectShortlist } from "@/services/project-report-service";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { AxConsultingResultTab } from "./components/ax-consulting-result-tab";
import { RequirementsDefinitionTab } from "./components/requirements-definition-tab";

import icArrowLeft from "@/assets/icons/ic-arrow-left.svg";
import icDatabase from "@/assets/icons/ic-database-02.svg";
import icSever from "@/assets/icons/ic-server-02.svg";
import icShield from "@/assets/icons/ic-shield-03.svg";

const SECTIONS = [
  { key: "4. 보안 및 시스템 연계 요구사항", icon: icShield },
  { key: "5. 데이터 및 데이터 파이프라인 요건", icon: icDatabase },
  { key: "6. Agent 적합성 필터 (요약)", icon: icSever },
];

export default function ProjectReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const showBackButton = location.state?.from === "list";
  const initialEditMode = location.state?.mode === "edit";

  const [report, setReport] = useState<ProjectReportType | null>(null);
  const [shortlistData, setShortlistData] = useState<ShortlistData | null>(null);
  const [readiness, setReadiness] = useState<ShortlistReadiness | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusProject, setStatusProject] = useState("");

  const fetchReportById = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const [reportRes, shortlistRes] = await Promise.all([
        getProjectReport(id),
        getProjectShortlist(id),
      ]);
      setReport(reportRes.data.data.content);
      setStatusProject(reportRes.data.data.project_status);
      setShortlistData(shortlistRes.data.data);
      setReadiness(shortlistRes.data.data.readiness);
    } catch (error) {
      console.error("Failed to fetch report:", error);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  const refreshShortlistOnly = useCallback(async () => {
    if (!id) return;
    try {
      const shortlistRes = await getProjectShortlist(id);
      setShortlistData(shortlistRes.data.data);
      setReadiness(shortlistRes.data.data.readiness);
    } catch (error) {
      console.error("Failed to refresh shortlist:", error);
    }
  }, [id]);

  useEffect(() => {
    fetchReportById();
  }, [id]);

  if (!report) {
    return null;
  }

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div
        className="lg:max-w-[1120px] px-5 min-[1120px]:px-0 mx-auto pt-6 lg:pt-10 w-full"
        style={{ display: isLoading ? "none" : "block" }}
      >
        {showBackButton && (
          <div className="w-full md:pb-6 pb-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="w-10 h-10 lg:w-12 lg:h-12"
            >
              <img src={icArrowLeft} alt="Icon Arrow Left" />
            </Button>
          </div>
        )}

        <Tabs defaultValue="requirements">
          <TabsList className="w-full grid grid-cols-2  md:flex md:gap-6 bg-transparent border-b border-[rgba(0,0,0,0.15)] h-auto rounded-none p-0 mb-4 md:mb-6 gap-3  md:justify-start">
            <TabsTrigger
              value="requirements"
              className="rounded-none border-b-2 border-transparent px-0 py-1 text-base font-bold leading-6 tracking-[-0.01em] text-[#B0B0B0] data-[state=active]:border-[#FD5108] data-[state=active]:bg-transparent data-[state=active]:text-[#000000] data-[state=active]:shadow-none"
            >
              요구사항 정의서
            </TabsTrigger>
            <TabsTrigger
              value="ax-consulting"
              className="rounded-none border-b-2 border-transparent px-0 py-1 text-base font-bold leading-6 tracking-[-0.01em] text-[#B0B0B0] data-[state=active]:border-[#FD5108] data-[state=active]:bg-transparent data-[state=active]:text-[#000000] data-[state=active]:shadow-none"
            >
              AX Readinees 진단결과
            </TabsTrigger>
          </TabsList>

          <TabsContent value="requirements">
            <RequirementsDefinitionTab
              id={id!}
              report={report}
              refresh={fetchReportById}
              status={statusProject}
              initialMode={initialEditMode ? "edit" : "view"}
            />
          </TabsContent>

          <TabsContent value="ax-consulting" className="mt-0">
            <AxConsultingResultTab
              shortlistData={shortlistData}
              readiness={readiness}
              id={id!}
              onRefresh={refreshShortlistOnly}
            />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
